import { apiClient } from '@/api/client';
import { createApiResource } from '@/api/resource';
import type { Activatable, BaseRecord } from '@/api/types';

/** Mirrors `StaffingRequestStatus` on the backend. */
export type StaffingRequestStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface StaffingRequestPosition extends BaseRecord {
  request: string;
  site_position: string;
  site_position_id: string;
  site_position_name: string;
  site_position_site: string;
  quantity: number;
  needed_date: string;
  schedule: string;
}

export interface StaffingRequestPositionInput {
  site_position: string;
  quantity: number;
  needed_date: string;
  schedule?: string;
}

export interface StaffingRequestReviewComment extends BaseRecord {
  review: string;
  comment: string;
}

export interface StaffingRequestReview extends BaseRecord {
  request: string;
  reviewer: string;
  reviewer_name: string;
  decision: string;
  reviewed_at: string | null;
  comments: StaffingRequestReviewComment[];
}

export interface StaffingRequest extends BaseRecord, Activatable {
  justification: string;
  suggestions: string;
  status: StaffingRequestStatus;
  status_display: string;
  requester: string;
  requester_name: string;
  positions: StaffingRequestPosition[];
  reviews: StaffingRequestReview[];
}

/**
 * The nested `positions` are create-only: the backend view pops them and the
 * service sets `request` on each row. `requester` never travels: the backend
 * infers it from the authenticated user (nobody raises someone else's request).
 */
export interface StaffingRequestInput {
  justification: string;
  suggestions?: string;
  positions: StaffingRequestPositionInput[];
}

export const staffingRequestsApi = {
  ...createApiResource<StaffingRequest, StaffingRequestInput>('/api/v1/staffing/requests'),

  /**
   * DRAFT → SUBMITTED. The state machine lives on the backend service; the
   * request naves SUBMITTED and only returns to DRAFT when a review observes
   * it, so this is how a corrected request goes back under review.
   */
  submit: (id: string) =>
    apiClient
      .post<StaffingRequest>(`/api/v1/staffing/requests/${id}/submit/`)
      .then(({ data }) => data),
};
